/** Angular Imports */
import { Component, effect, inject, input, output, signal, untracked } from '@angular/core';

/** Third Party Imports */
import { Button, Modal } from '@ntv360/component-pantry';

/** Core Imports */
import { ApiLicense, LicenseQueryParams } from '@core/services/licenses/licenses.interfaces';
import { LicensesService } from '@core/services/licenses/licenses.service';

/** Shared Imports */
import { AutocompleteComponent } from '@shared/components/autocomplete/autocomplete.component';
import { Logger } from '@shared/utils';

/** Local Imports */
import { LICENSE_SEARCH_FIELDS } from '@features/licenses/constants/licenses-table.constants';

export interface AssignLicenseForm {
    licenseId: string | null;
}

@Component({
    selector: 'app-assign-license-modal',
    standalone: true,
    imports: [Modal, Button, AutocompleteComponent],
    templateUrl: './assign-license-modal.component.html',
    styleUrl: './assign-license-modal.component.scss',
})
export class AssignLicenseModalComponent {
    /** Service for fetching license data. */
    private licensesService = inject(LicensesService);

    /** Controls modal visibility. Required by the parent. */
    public isVisible = input.required<boolean>();

    /**
     * License IDs to exclude from the dropdown options.
     * Accepts a single ID or array of IDs.
     */
    public excludeLicenseIds = input<string | string[] | undefined>(undefined);

    /**
     * Owner (dealer) ID used to scope the license list to a single dealer.
     * When provided, only licenses with this `ownerId` are fetched.
     */
    public ownerId = input<string | undefined>(undefined);

    /** Emitted when the modal is closed by the user. */
    public modalClose = output<void>();

    /** Emitted when the user confirms assignment with a selected license. */
    public assign = output<AssignLicenseForm>();

    /** Flat list of license options rendered in the autocomplete dropdown. */
    public licenseOptions = signal<{ label: string; value: string }[]>([]);

    /** True while the initial page of licenses is being fetched. */
    public isLoadingLicenses = signal(false);

    /** True while an additional page of licenses is being appended (infinite scroll). */
    public isLoadingMore = signal(false);

    /** True while the assign API call is in flight. */
    public isAssigning = signal(false);

    /** Current pagination page for the license list. */
    private currentPage = signal(1);

    /** Whether the license API has a next page available for infinite scroll. */
    private hasNextPage = signal(false);

    /** Current search term used to query licenses. */
    private searchTerm = signal('');

    /** Tracks the autocomplete's selected items for two-way binding and reset on close. */
    public selectedLicenseOptions = signal<{ label: string; value: string }[]>([]);

    /** Current form state holding the selected license ID. */
    public form = signal<AssignLicenseForm>({ licenseId: null });

    /** Static config passed to the license autocomplete. */
    public autocompleteConfig = { placeholder: 'Search license', searchable: true };

    constructor() {
        effect(() => {
            const visible = this.isVisible();
            this.excludeLicenseIds();
            this.ownerId();
            untracked(() => {
                if (!visible) {
                    this.form.set({ licenseId: null });
                    this.selectedLicenseOptions.set([]);
                    this.isAssigning.set(false);
                    this.currentPage.set(1);
                    this.hasNextPage.set(false);
                    this.searchTerm.set('');
                    return;
                }
                this.loadLicenses(this.searchTerm());
            });
        });
    }

    /** True when a license is selected. */
    get isFormValid(): boolean {
        return !!this.form().licenseId;
    }

    /**
     * Fetches the first page of licenses and populates the autocomplete options.
     * Also seeds the pagination state.
     */
    private loadLicenses(search = ''): void {
        this.isLoadingLicenses.set(true);
        const params = this.buildParams(1, search);

        this.licensesService.getAll(params).subscribe({
            next: (res) => {
                this.licenseOptions.set(res.data.map((l) => this.toOption(l)));
                this.hasNextPage.set(res.meta.pagination.hasNextPage);
                this.currentPage.set(1);
                this.isLoadingLicenses.set(false);
            },
            error: (err) => {
                Logger.error('AssignLicenseModal', 'Failed to load licenses', err);
                this.isLoadingLicenses.set(false);
            },
        });
    }

    /**
     * Triggered by the autocomplete's scroll-end event.
     * Fetches the next page of licenses and appends them to the existing options.
     */
    protected onScrollEnd(): void {
        if (!this.hasNextPage() || this.isLoadingMore()) return;
        this.isLoadingMore.set(true);
        const nextPage = this.currentPage() + 1;
        const params = this.buildParams(nextPage, this.searchTerm());

        this.licensesService.getAll(params).subscribe({
            next: (res) => {
                const newOptions = res.data.map((l) => this.toOption(l));
                this.licenseOptions.update((opts) => [...opts, ...newOptions]);
                this.hasNextPage.set(res.meta.pagination.hasNextPage);
                this.currentPage.set(nextPage);
                this.isLoadingMore.set(false);
            },
            error: (err) => {
                Logger.error('AssignLicenseModal', 'Failed to load more licenses', err);
                this.isLoadingMore.set(false);
            },
        });
    }

    /**
     * Triggered by the autocomplete's debounced search.
     * Refetches the license list using the current search term.
     */
    protected onSearch(term: string): void {
        this.searchTerm.set(term);
        this.loadLicenses(term);
    }

    /**
     * Updates the form's `licenseId` when the user selects a license from the autocomplete.
     */
    protected onLicenseChange(event: { label: string; value: string } | null): void {
        this.form.update((f) => ({ ...f, licenseId: event?.value ?? null }));
    }

    /**
     * Emits the current form to the parent and sets `isAssigning` to block further interaction.
     */
    protected onAssign(): void {
        if (!this.isFormValid) return;
        this.isAssigning.set(true);
        this.assign.emit(this.form());
    }

    /**
     * Emits `modalClose` unless an assignment is in flight.
     */
    protected onClose(): void {
        if (this.isAssigning()) return;
        this.modalClose.emit();
    }

    /**
     * Maps a raw API license to a flat `{ label, value }` option for the autocomplete.
     */
    private toOption(license: ApiLicense): { label: string; value: string } {
        return {
            label: license.alias || license.licenseKey || '—',
            value: license.licenseId ?? '',
        };
    }

    /**
     * Normalizes the `excludeLicenseIds` input into a deduped string array.
     */
    private getExcludeArray(): string[] {
        const raw = this.excludeLicenseIds();
        if (!raw) return [];
        const arr = Array.isArray(raw) ? raw : [raw];
        return [...new Set(arr.filter(Boolean))];
    }

    /**
     * Builds the licenses query params, applying the `_nin` filter when there
     * are licenses to exclude.
     */
    private buildParams(page: number, search: string): LicenseQueryParams {
        const exclude = this.getExcludeArray();
        const owner = this.ownerId();
        const filters: Record<string, unknown> = {};
        if (exclude.length) filters['licenseId'] = { _nin: exclude };
        if (owner) filters['ownerId'] = { _eq: owner };
        return {
            page,
            limit: 100,
            search: search || undefined,
            searchFields: search ? LICENSE_SEARCH_FIELDS.join(',') : undefined,
            filters: Object.keys(filters).length ? filters : undefined,
        } as LicenseQueryParams;
    }
}
